// Shared types for contest results views

export interface ContestDriver {
  position: string; // A, B, C, D, E (group)
  driverId: string;
  driverName: string;
  driverSlug?: string; // Used for driver image: /images/drivers/{slug}.jpg
  driverNumber: string;
  driverManufacturer?: string | null; // Ford, Chevrolet, Toyota
  // Server-resolved paint-scheme path for this specific (user, position)
  // pick. Populated by the league tiebreaker resolver so the pack view
  // can show a distinct paint scheme per user when multiple users picked
  // the same driver. Null when the number isn't in car_images.
  carImagePath?: string | null;
  salary: number;
  finishPosition?: number;
  points: number;
  // For events with qualifying heats (like the Clash)
  // null = regular race, true = qualified for main, false = DNQ
  qualified?: boolean | null;
}

export interface ContestStanding {
  [key: string]: unknown;
  id: string;
  rank: number;
  username: string;
  isBot?: boolean;
  avatarUrl?: string | null;
  supporterBadge?: string | null; // 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond'
  customCarImage?: string | null; // Custom car image path for supporters
  // ISO timestamp of when this user submitted their lineup. Used for the
  // PackView tiebreaker: when multiple users pick the same A driver, the
  // earliest submitter wins the paint scheme; everyone else falls to B.
  // Stable across the race — score changes don't swap cars underneath you.
  lineupSubmittedAt?: string | null;
  score: number;
  winnings?: number;
  drivers: ContestDriver[];
}

export interface RaceResultEntry {
  driverId: string;
  carNumber: string;
  driverName: string;
  finishPosition: number;
  startingPosition: number;
  lapsCompleted: number;
  lapsLed: number;
  finalStatus: string;
}

export interface ContestResultsData {
  // Contest metadata
  contest: {
    id: string;
    scheduleId?: string | null;
    name: string;
    slug: string;
    trackName: string;
    raceName?: string;
    raceDate?: string;
    entryFee: number;
    prize: number;
    entries: number;
    humanEntries?: number;
    botEntries?: number;
    status: string;
    contestType?: string;
    payoutsSettled?: boolean;
    winningsSource?: "ledger";
    totalLaps?: number;
    nascarRaceId?: number | null;
    nascarSeriesId?: number | null;
    prizePositions?: number; // Number of positions that win coins (if not set, calculated from entries)
    series?: string; // "cup" | "oreilly" | "truck"
  };

  // Navigation context
  backLink: {
    label: string;
    to: string;
    params?: Record<string, string>;
  };

  // Standings with driver details
  standings: ContestStanding[];

  // Full race results for leaderboard display
  raceResults?: RaceResultEntry[];

  // Current user's entry (if they have one)
  userEntry?: {
    id: string;
    rank: number;
    score: number;
  };

  // Custom scoring info for leagues (null = using default scoring)
  customScoring?: {
    enabled: boolean;
    categories: string[];
    // Full scoring rules for client-side live scoring
    scoringRules?: Array<{ category: string; points: number; enabled: boolean }>;
  } | null;

  // Paint scheme resolved from car_images for this specific race, keyed by
  // car number ("12" → "/assets/cars/12-menards.png"). When present, the pack
  // view prefers these over the static CAR_VARIANTS map so cars on screen
  // match the paint schemes running on the track this week. Populated by the
  // contest loader via getPaintSchemeMapForSchedule — absence means fall
  // back to the legacy static variant pool.
  raceEntryCarImages?: Record<string, string>;
}

// Group colors used across views - using CSS variables for consistency
export const GROUP_COLORS: Record<string, { bg: string; text: string; bar: string; posText: string }> = {
  A: { bg: "bg-[var(--group-a-bg)]", text: "text-white", bar: "var(--group-a-bg)", posText: "var(--leaderboard-pos-text)" },
  B: { bg: "bg-[var(--group-b-bg)]", text: "text-white", bar: "var(--group-b-bg)", posText: "#FDF8E8" },
  C: { bg: "bg-[var(--group-c-bg)]", text: "text-white", bar: "var(--group-c-bg)", posText: "#FFFFFA" },
  D: { bg: "bg-[var(--group-d-bg)]", text: "text-white", bar: "var(--group-d-bg)", posText: "#FFFEFA" },
  E: { bg: "bg-[#E91101]", text: "text-white", bar: "#E91101", posText: "#FFFDF5" },
  // GPP contest positions (D1-D5)
  D1: { bg: "bg-[var(--group-a-bg)]", text: "text-white", bar: "var(--group-a-bg)", posText: "var(--leaderboard-pos-text)" },
  D2: { bg: "bg-[var(--group-b-bg)]", text: "text-white", bar: "var(--group-b-bg)", posText: "#FDF8E8" },
  D3: { bg: "bg-[var(--group-c-bg)]", text: "text-white", bar: "var(--group-c-bg)", posText: "#FFFFFA" },
  D4: { bg: "bg-[var(--group-d-bg)]", text: "text-white", bar: "var(--group-d-bg)", posText: "#FFFEFA" },
  D5: { bg: "bg-[#E91101]", text: "text-white", bar: "#E91101", posText: "#FFFDF5" },
  // Showdown (Captain mode) positions
  CPT: { bg: "bg-[#eab308]", text: "text-black", bar: "#eab308", posText: "#000" }, // Gold for captain
  UTIL: { bg: "bg-[#6b7280]", text: "text-white", bar: "#6b7280", posText: "#fff" }, // Gray for utility
};

// Flag state configuration - colors chosen for WCAG AA contrast (4.5:1 minimum)
export const FLAG_CONFIG: Record<string, { bg: string; text: string; label: string }> = {
  green: { bg: "#15803d", text: "#fff", label: "GREEN" },
  caution: { bg: "#ca8a04", text: "#000", label: "CAUTION" },
  red: { bg: "#b91c1c", text: "#fff", label: "RED FLAG" },
  white: { bg: "#fff", text: "#000", label: "WHITE FLAG" },
  checkered: { bg: "#1a1a1a", text: "#fff", label: "CHECKERED" },
  "pre-race": { bg: "#4b5563", text: "#fff", label: "PRE-RACE" },
};

export interface OvertimeEvent {
  triggerLap: number;
  cautionLaps: number;
  greenLap: number;
  result: "caution" | "clean";
}

// Calculate the number of prize positions based on total entries
// Follows typical fantasy sports payout structures
export function calculatePrizePositions(entries: number, prizePositions?: number): number {
  // If explicitly set, use that value
  if (prizePositions !== undefined && prizePositions > 0) {
    return Math.min(prizePositions, entries);
  }

  // Default calculation based on entry count
  if (entries <= 2) return 1;
  if (entries <= 5) return 1;
  if (entries <= 10) return 2;
  if (entries <= 20) return 3;
  if (entries <= 35) return 4;
  if (entries <= 50) return 5;
  // For larger contests, ~15% of entries win
  return Math.max(5, Math.floor(entries * 0.15));
}

export interface PrizeBreakdownEntry {
  position: number;
  amount: number; // In coins (rounded up to nearest 10)
}

/** Round up to nearest multiple of 10 */
function roundUp10(n: number): number {
  return Math.ceil(n / 10) * 10;
}

/**
 * GPP payout tier definition.
 * Each tier specifies the end position and what percentage of the pool each person in that tier gets.
 * Tiers are cumulative — positions not covered by earlier tiers fall into the next one.
 */
interface PayoutTier {
  /** Last position in this tier (inclusive). Use Infinity for "rest". */
  through: number;
  /** Percentage of total pool each person in this tier receives. */
  pctPerPerson: number;
}

/**
 * Standard GPP payout tiers (percentage of pool per person).
 * Designed so that with typical prize position counts (15-30% of field),
 * the total allocation lands close to 100%.
 *
 * Example with 55 paid positions, 50000 pool:
 *   1st: 10% = 5000    2nd: 8% = 4000    3rd: 6% = 3000
 *   4th-5th: 4% = 2000 each    6th-8th: 3% = 1500 each
 *   9th-14th: 2% = 1000 each   15th-26th: 1.5% = 750 each
 *   27th+: 1% = 500 each
 */
const GPP_TIERS: PayoutTier[] = [
  { through: 1, pctPerPerson: 10 },
  { through: 2, pctPerPerson: 8 },
  { through: 3, pctPerPerson: 6 },
  { through: 5, pctPerPerson: 4 },
  { through: 8, pctPerPerson: 3 },
  { through: 14, pctPerPerson: 2 },
  { through: 26, pctPerPerson: 1.5 },
  { through: Infinity, pctPerPerson: 1 },
];

/**
 * Calculate prize breakdown for each position.
 *
 * For double-ups (flat payout), everyone gets equal share.
 * For GPP contests, uses tiered payout structure matching standard fantasy sports
 * prize distributions with top-heavy payouts.
 *
 * All amounts are rounded to the nearest multiple of 10.
 */
export function calculatePrizeBreakdown(
  totalPrize: number,
  prizePositions: number,
  entryFee: number,
): PrizeBreakdownEntry[] {
  if (prizePositions <= 0) return [];

  // Double-up detection: if prize per person ~= 2x entry fee, it's a flat payout.
  const prizePerPerson = totalPrize / prizePositions;
  const isDoubleUp = Math.abs(prizePerPerson - entryFee * 2) < entryFee * 0.5;

  if (isDoubleUp || prizePositions <= 2) {
    const perPosition = roundUp10(totalPrize / prizePositions);
    return Array.from({ length: prizePositions }, (_, i) => ({
      position: i + 1,
      amount: perPosition,
    }));
  }

  // GPP-style tiered distribution
  // Step 1: Assign raw amounts from tier percentages
  const rawAmounts: number[] = [];
  for (let pos = 1; pos <= prizePositions; pos++) {
    const tier = GPP_TIERS.find((t) => pos <= t.through);
    const pct = tier ? tier.pctPerPerson : GPP_TIERS[GPP_TIERS.length - 1].pctPerPerson;
    rawAmounts.push((pct / 100) * totalPrize);
  }

  // Step 2: Scale to fit the total prize pool exactly, then round to nearest 10
  const rawTotal = rawAmounts.reduce((sum, a) => sum + a, 0);
  const scale = totalPrize / rawTotal;

  const breakdown: PrizeBreakdownEntry[] = rawAmounts.map((raw, i) => ({
    position: i + 1,
    amount: round10(raw * scale),
  }));

  // Step 3: Adjust for rounding error — add/subtract from 1st place
  const roundedTotal = breakdown.reduce((sum, b) => sum + b.amount, 0);
  const diff = totalPrize - roundedTotal;
  if (diff !== 0 && breakdown.length > 0) {
    breakdown[0].amount += diff;
  }

  return breakdown;
}

/** Round to nearest multiple of 10 */
function round10(n: number): number {
  return Math.round(n / 10) * 10;
}

/**
 * Calculate actual winnings for each standing, accounting for ties.
 *
 * When players tie across prize boundaries, affected prizes are pooled
 * and split evenly among all tied players.
 *
 * Example: top 8 win, 5 players tie for 7th place.
 * Pool prizes for 7th + 8th, divide by 5, round up to nearest 10.
 *
 * Returns a Map of standing index → winning amount (in coins).
 */
export function calculateActualWinnings(
  standings: Array<{ id: string; score: number }>,
  breakdown: PrizeBreakdownEntry[],
): Map<string, number> {
  const winnings = new Map<string, number>();
  if (standings.length === 0 || breakdown.length === 0) return winnings;

  const maxPrizePos = breakdown.length;

  // Group consecutive standings by score into tie groups
  // Each group: { startRank (1-indexed), ids, score }
  const tieGroups: Array<{ startRank: number; ids: string[]; score: number }> = [];
  let currentGroup: { startRank: number; ids: string[]; score: number } | null = null;

  for (let i = 0; i < standings.length; i++) {
    const standing = standings[i];
    if (currentGroup && standing.score === currentGroup.score) {
      currentGroup.ids.push(standing.id);
    } else {
      if (currentGroup) tieGroups.push(currentGroup);
      currentGroup = { startRank: i + 1, ids: [standing.id], score: standing.score };
    }
  }
  if (currentGroup) tieGroups.push(currentGroup);

  // For each tie group, calculate the payout
  for (const group of tieGroups) {
    const { startRank, ids } = group;
    const endRank = startRank + ids.length - 1;

    // If the entire group is outside prize positions, they win 0
    if (startRank > maxPrizePos) {
      for (const id of ids) winnings.set(id, 0);
      continue;
    }

    // Pool prizes from startRank to min(endRank, maxPrizePos)
    let pooledPrize = 0;
    for (const entry of breakdown) {
      if (entry.position >= startRank && entry.position <= endRank) {
        pooledPrize += entry.amount;
      }
    }

    // Split evenly, round up to nearest 10
    const perPlayer = ids.length > 0 ? roundUp10(pooledPrize / ids.length) : 0;
    for (const id of ids) winnings.set(id, perPlayer);
  }

  return winnings;
}

export interface OvertimeScenario {
  hasOvertime: boolean;
  overtimeEvents: OvertimeEvent[];
  finalLap: number;
}
